import {
  Button,
  ButtonProps,
  HFlow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderContentProps,
  ModalProps,
} from 'bold-ui'
import { composeHandlers } from 'bold-ui/lib/util/react'
import { Form, FormRenderProps, SubmitButton } from 'components/form'
import { Decorator, ValidationErrors } from 'final-form'
import React from 'react'

interface FormModalButtonProps extends ButtonProps {
  label: string
}

interface FormModalProps<T> extends ModalHeaderContentProps, Omit<ModalProps, 'title' | 'onSubmit'> {
  renderBody(formProps: FormRenderProps<T>): React.ReactNode
  validate?(values: T): ValidationErrors | Promise<ValidationErrors>
  onSubmit(values: T): void
  decorators: Array<Decorator<T>>

  initialValues?: T

  onClose(): void

  buttons: {
    confirm: string | FormModalButtonProps
    cancel: string | FormModalButtonProps
  }
}

export default function FormModal<T extends object>(props: Readonly<FormModalProps<T>>) {
  const {
    initialValues,
    buttons,
    onSubmit,
    renderBody,
    validate,
    onClose,
    modalProps,
    modalHeaderProps,
    decorators,
  } = resolveProps(props)

  const {
    confirm: { label: confirmLabel, ...confirmProps },
    cancel: { label: cancelLabel, ...cancelProps },
  } = buttons

  const renderForm = (formProps: FormRenderProps<T>) => {
    const { handleSubmit } = formProps
    const modalBody = renderBody(formProps)

    return (
      <Modal onClose={onClose} {...modalProps}>
        <ModalHeader {...modalHeaderProps} />
        <ModalBody>{modalBody}</ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button {...cancelProps} onClick={composeHandlers(cancelProps.onClick, onClose)}>
              {cancelLabel}
            </Button>
            <SubmitButton title={confirmLabel} kind='primary' handleSubmit={handleSubmit} {...confirmProps}>
              {confirmLabel}
            </SubmitButton>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<T>
      render={renderForm}
      validate={validate}
      onSubmit={onSubmit}
      initialValues={initialValues}
      decorators={decorators}
    />
  )
}

FormModal.defaultProps = {
  closeOnBackdropClick: false,
  size: 'small',
  buttonLabels: {
    confirm: 'Salvar',
    cancel: 'Cancelar',
  },
} as Partial<FormModalProps<any>>

const resolveProps = <T extends object>(props: FormModalProps<T>) => {
  const {
    initialValues,
    buttons,
    onSubmit,
    renderBody,
    validate,
    onClose,
    open,
    size,
    children,
    containerRef,
    depthLevel,
    manageOverflow,
    closeOnBackdropClick,
    decorators,
    title,
    icon,
    subtitle,
  } = props

  const normalizedButtons: { confirm: FormModalButtonProps; cancel: FormModalButtonProps } = {
    confirm: typeof buttons.confirm === 'string' ? { label: buttons.confirm } : buttons.confirm,
    cancel: typeof buttons.cancel === 'string' ? { label: buttons.cancel } : buttons.cancel,
  }

  return {
    initialValues,
    buttons: normalizedButtons,
    onSubmit,
    renderBody,
    validate,
    onClose,
    decorators,
    modalProps: { open, size, children, containerRef, depthLevel, manageOverflow, closeOnBackdropClick },
    modalHeaderProps: {
      title,
      subtitle,
      icon,
    },
  }
}
